import React, { useEffect, useState } from "react";
import Button from "../../../../components/ui/Button";
import Card from "../../../../components/ui/card";
import PhoneInput from "../../../../components/ui/PhoneInput";
import { Edit, Mail, Phone, MessageCircle } from "lucide-react";
import { useSelector } from "react-redux";
import { UpdateUserProfile, useAppDispatch } from "../../../../redux";
import { useTranslation } from "react-i18next";

export default function AboutComponent({
    isEditing,
    startEditing,
    handleFieldChange,
    editedProfile,
    profileData,
    validationErrors,
    handleCancelEdit,
    handleSaveChanges,
    isCurrentUser = true,
    canEdit = true,
    shouldMaskSensitiveInfo = false,
}: any) {
    const { t } = useTranslation();
    
    // Function to handle "Use phone number" button click
    const handleUsePhoneNumber = () => {
        if (editedProfile.phone) {
            // Copy phone number to WhatsApp field and enable WhatsApp contact
            handleFieldChange("whatsappNumber", editedProfile.phone);
            handleFieldChange("contactOnWhatsapp", true);
        }
    };

    return (
        <Card color={undefined} className="p-4 w-full">
            <h2 className="text-lg font-semibold mb-4 flex items-center justify-between">
                {t("aboutComponent.title")}
                {!isEditing && isCurrentUser && canEdit && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                            startEditing();
                        }}
                        className="text-blue-600 hover:text-blue-700">
                        <Edit className="h-4 w-4" />
                    </Button>
                )}
            </h2>
            {isEditing ? (
                <div className="space-y-4">
                    {/* Show notification if user has no phone */}
                    {!profileData.personal.phone && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                            <div className="flex items-start">
                                <div className="flex-shrink-0">
                                    <Phone className="h-5 w-5 text-blue-400" />
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-blue-800">
                                        {t("aboutComponent.completeYourProfile")}
                                    </h3>
                                    <div className="mt-1 text-sm text-blue-700">
                                        <p>{t("aboutComponent.pleaseAddInfo")}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("aboutComponent.name")}</label>
                        <input
                            type="text"
                            value={editedProfile.name}
                            onChange={e => handleFieldChange("name", e.target.value)}
                            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                        {validationErrors.name && <p className="text-red-500 text-xs mt-1">{validationErrors.name}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("aboutComponent.title")}</label>
                        <input
                            type="text"
                            value={editedProfile.title}
                            onChange={e => handleFieldChange("title", e.target.value)}
                            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                        {validationErrors.title && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.title}</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("aboutComponent.bio")}</label>
                        <textarea
                            value={editedProfile.bio}
                            maxLength={450}
                            onChange={e => handleFieldChange("bio", e.target.value)}
                            className="w-full p-2 border rounded-lg min-h-[100px] focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                        {validationErrors.bio && <p className="text-red-500 text-xs mt-1">{validationErrors.bio}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("aboutComponent.email")}</label>
                        <input
                            type="email"
                            value={editedProfile.email}
                            onChange={e => handleFieldChange("email", e.target.value)}
                            disabled
                            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("aboutComponent.phone")}</label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <span className="text-black-500 text-sm">+</span>
                            </div>
                            <input
                                type="tel"
                                value={editedProfile.phone.replace(/^\+/, "")}
                                onChange={(e) => {
                                    let value = e.target.value.replace(/\D/g, "");
                                    if (value) {
                                        value = "+" + value;
                                    }
                                    handleFieldChange("phone", value);
                                }}
                                placeholder={t("aboutComponent.phonePlaceholder")}
                                className="w-full pl-8 pr-3 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                        </div>
                        {validationErrors.phone && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.phone}</p>
                        )}
                    </div>
                    
                    {/* WhatsApp Contact Preferences */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="contactOnWhatsapp"
                                    checked={editedProfile.contactOnWhatsapp || false}
                                    onChange={e => handleFieldChange("contactOnWhatsapp", e.target.checked)}
                                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                />
                                <label htmlFor="contactOnWhatsapp" className="text-sm font-medium text-gray-900">
                                    {t("aboutComponent.wantWhatsappContact")}
                                </label>
                            </div>
                            <button
                                type="button"
                                onClick={handleUsePhoneNumber}
                                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                            >
                                {t("aboutComponent.usePhoneNumber")}
                            </button>
                        </div>
                        
                        {editedProfile.contactOnWhatsapp && (
                            <div>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span className="text-black-500 text-sm">+</span>
                                    </div>
                                    <input
                                        type="tel"
                                        value={(editedProfile.whatsappNumber || "").replace(/^\+/, "")}
                                        onChange={(e) => {
                                            let value = e.target.value.replace(/\D/g, "");
                                            if (value) {
                                                value = "+" + value;
                                            }
                                            handleFieldChange("whatsappNumber", value);
                                        }}
                                        placeholder={t("aboutComponent.phonePlaceholder")}
                                        className="w-full pl-8 pr-3 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                    />
                                    <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                                        <div className="w-5 h-5 bg-green-500 rounded flex items-center justify-center">
                                            <MessageCircle className="w-3 h-3 text-white" />
                                        </div>
                                    </div>
                                </div>
                                {validationErrors.whatsappNumber && (
                                    <p className="text-red-500 text-xs mt-1">{validationErrors.whatsappNumber}</p>
                                )}
                            </div>
                        )}
                        
                        <div className="text-gray-500 text-xs space-y-1">
                            <p>{t("aboutComponent.emergencyNote")}</p>
                            <p className="flex items-start gap-1">
                                <span className="text-gray-400">•</span>
                                {t("aboutComponent.canInsertLater")}
                            </p>
                        </div>
                    </div>
                    
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={handleCancelEdit} className="text-sm">
                            {t("aboutComponent.cancel")}
                        </Button>
                        <Button onClick={handleSaveChanges} className="text-sm">
                            {t("aboutComponent.save")}
                        </Button>
                    </div>
                </div>
            ) : (
                <div>
                    <p className="text-gray-600 text-sm mb-4">{profileData.personal.bio}</p>
                    <div className="pt-4 border-t space-y-3 text-sm">
                        <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-gray-400" />
                            <span>{shouldMaskSensitiveInfo ? "***@***.***" : profileData.personal.email}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span>{shouldMaskSensitiveInfo ? "***" : profileData.personal.phone}</span>
                        </div>
                        {profileData.personal.contactOnWhatsapp && profileData.personal.whatsappNumber && (
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 bg-green-500 rounded flex items-center justify-center">
                                    <MessageCircle className="w-3 h-3 text-white" />
                                </div>
                                <span>{shouldMaskSensitiveInfo ? "***" : profileData.personal.whatsappNumber}</span>
                                <span className="text-gray-400">{t("aboutComponent.whatsapp")}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Card>
    );
}
